import styled from 'styled-components';
import {Radio} from 'antd';
import AntdModal from '../../../AntdModal';
import {bordersStyles} from '../../../../utils/styles';

// Height must be dynamic, not a guessed pixel number (design review: "the modal is too tall, I
// have to scroll the page"). `.ant-modal-content` becomes a column flexbox capped at a viewport
// fraction, so the header/Steps bar/footer stay put and only the step body (`.ant-modal-body`)
// scrolls when a step's own content (step 3's column table, in particular) is taller than that.
const StyledExportWizard = styled(AntdModal)`
  .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 120px);
  }

  .ant-modal-body {
    overflow-y: auto;
    flex: 1 1 auto;
  }

  .ant-picker {
    width: 100%;
  }

  // antd's own Form.Item label colour reads as too light next to the rest of the wizard's copy
  // ("Data from" / "Period" / "File type") -- darkened to match every other primary label here.
  .ant-form-item-label > label {
    color: ${(props) => props.theme.colors.neutral_10};
  }
`;

// Each step's own content, in normal flow (no fixed height, no absolute positioning) -- only the
// active step is rendered at all (ExportWizard.jsx), so this never needs to know the tallest
// step's height up front the way the old DrawerFormSteps-based layout did.
const StyledExportWizardStepPanel = styled.div`
  padding-top: 8px;
`;

// Shared by the progress ("Preparing...") and done (file name + scope/period) states -- both are
// the same centred, padded panel that replaces the form once submission starts.
const StyledExportWizardOverlay = styled.div`
  padding: 24px 0;
  text-align: center;
`;

const StyledExportWizardRecap = styled.div`
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
  margin-top: 8px;
`;

// "Data from" / "Period" / step 3's "File type" (examples/03, /04): separate gapped pills, not
// antd Segmented's contiguous sliding bar -- bug report scenario 3/7, "bubbles are missing". Built
// on Radio.Group's own `options` prop (same shape Segmented took, so callers barely change) rather
// than a bespoke component, so keyboard/aria semantics stay native.
const StyledExportWizardPillGroup = styled(Radio.Group)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .ant-radio-button-wrapper {
    border-radius: 999px !important;
    border: 1px solid ${(props) => props.theme.colors.neutral_4} !important;
    color: ${(props) => props.theme.colors.neutral_8};
    padding-inline: 16px;

    // Radio.Button's default styling joins adjacent buttons into one bar (shared borders, a
    // ::before divider on every button but the first) -- undone here so each option reads as its
    // own standalone pill, matching the gapped layout above.
    &::before {
      display: none;
    }

    &.ant-radio-button-wrapper-checked {
      border-color: ${(props) => props.theme.colors.primary_6} !important;
      color: ${(props) => props.theme.colors.primary_6};

      &:hover {
        border-color: ${(props) => props.theme.colors.primary_6} !important;
        color: ${(props) => props.theme.colors.primary_6};
      }
    }

    &.ant-radio-button-wrapper-disabled {
      color: ${(props) => props.theme.colors.neutral_7};
    }
  }
`;

// The loading state's "progressive loading bar" (bug report scenario 2) -- `downloadWithBackgroundJob`
// polls with no real percentage to report, so this is an indeterminate sliding fill, not a
// true 0-100% bar; same dash colour as the step-progress bar above for visual consistency.
const StyledExportWizardProgress = styled.div`
  padding: 24px 0;
  text-align: center;

  h3 {
    margin: 0 0 16px;
    font-size: 16px;
    font-weight: 600;
  }
`;

const StyledExportWizardProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 3px;
  background-color: #E9E9E9;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -35%;
    width: 35%;
    height: 100%;
    border-radius: 3px;
    background-color: ${(props) => props.theme.colors.secondary};
    animation: exportWizardProgressSlide 1.1s ease-in-out infinite;
  }

  @keyframes exportWizardProgressSlide {
    0% { left: -35%; }
    100% { left: 100%; }
  }
`;

// The receipts toggle (examples/03): label and Switch share one row, with the "always on"
// explanation directly under the label rather than as a detached paragraph below the field --
// boxed like a tile (bordersStyles.default) to read as one self-contained control.
const StyledExportWizardSwitchRow = styled.div`
  ${bordersStyles.default};

  margin-top: 16px;
  padding: 12px 16px;

  .switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .switch-row-label {
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${(props) => props.theme.colors.neutral_10};
    font-weight: 500;
  }

  // The explanation that used to sit below the label as its own paragraph now lives in a tooltip
  // on this icon instead (design review: the switch row was getting too tall with both toggles'
  // explanations always visible).
  .switch-row-info {
    color: ${(props) => props.theme.colors.neutral_8};
    font-size: 14px;
    cursor: help;
  }
`;

// "Step X of Y" + the step's own heading, sat above the dot-progress bar (mock: "Step 1 of 3" /
// "What do you want to export?").
const StyledExportWizardStepHeader = styled.div`
  margin-bottom: 12px;

  .step-count {
    color: ${(props) => props.theme.colors.neutral_8};
    font-size: 12px;
  }

  h3 {
    color: ${(props) => props.theme.colors.neutral_10};
    margin: 2px 0 0;
    font-size: 18px;
    font-weight: 600;
  }
`;

// The footer's own layout: a left-side recap (the chosen source/period, mock's "Marco Rossi ·
// September 2026") plus the right-aligned button group -- antd's `footer` prop takes any node, not
// just a button array, so this replaces the plain array the wizard used to return.
const StyledExportWizardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  .recap {
    color: ${(props) => props.theme.colors.neutral_8};
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .buttons {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    margin-left: auto;
  }
`;

const StyledExportWizardReady = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  h3 {
    margin: 4px 0 0;
    font-size: 16px;
    font-weight: 600;
  }

  span {
    color: ${(props) => props.theme.colors.neutral_8};
    font-size: 13px;
  }
`;

const StyledExportWizardReadyIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.success_25};
  color: ${(props) => props.theme.colors.functional_success};
  font-size: 22px;
`;

// Same shape as StyledExportWizardReadyIcon above, a neutral/info pair instead of success -- the
// "this one is taking longer, we'll email you" state (ExportWizard.jsx's `pendingFileName`) isn't
// a completed download, so it reads as in-progress rather than done.
const StyledExportWizardPendingIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.primary_1};
  color: ${(props) => props.theme.colors.primary_7};
  font-size: 22px;
`;

// The Accounts-tab Source Select's `optionRender` (plan addendum: show each OB account's balance,
// right-aligned, next to its name) -- same "name left / amount right" flex row as
// AccountBalanceDropdown/TeamSelect use elsewhere in the app.
const StyledExportWizardSourceOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  .source-option-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .source-option-balance {
    flex-shrink: 0;
    color: ${(props) => props.theme.colors.neutral_8};
  }
`;

export {
  StyledExportWizard,
  StyledExportWizardFooter,
  StyledExportWizardOverlay,
  StyledExportWizardPendingIcon,
  StyledExportWizardPillGroup,
  StyledExportWizardProgress,
  StyledExportWizardProgressBar,
  StyledExportWizardReady,
  StyledExportWizardReadyIcon,
  StyledExportWizardRecap,
  StyledExportWizardSourceOption,
  StyledExportWizardStepHeader,
  StyledExportWizardStepPanel,
  StyledExportWizardSwitchRow,
};
